import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { loginRoute } from '../../router';

import { signInWithEmail } from './auth-api';
import { sessionQueryKey } from './session';

/**
 * Ekran logowania (§7.3 wireframe, §5 proces „Logowanie", §17.1/§17.2).
 *
 * §5/§17.2: JEDEN komunikat błędu niezależnie od przyczyny (dane błędne, limit prób, błąd
 * serwera) — nigdy nie ujawnia, czy podane konto istnieje.
 */
const GENERIC_ERROR_MESSAGE =
  'Nie udało się zalogować. Sprawdź e-mail i hasło albo spróbuj ponownie za chwilę.';

function isSafeInternalPath(candidate: string): boolean {
  return candidate.startsWith('/') && !candidate.startsWith('//') && !candidate.includes('://');
}

export function LoginPage() {
  const search = loginRoute.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordHelp, setShowForgotPasswordHelp] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // §5: „wysłanie ma blokadę wielokrotnego kliknięcia" — ref (nie tylko `mutation.isPending`)
  // sprawdzany SYNCHRONICZNIE na starcie handlera, żeby dwa kliknięcia w tej samej chwili (przed
  // jakimkolwiek ponownym renderem) nigdy nie wywołały dwóch żądań sieciowych.
  const isSubmittingRef = useRef(false);
  const errorSummaryRef = useRef<HTMLDivElement | null>(null);
  const errorSummaryId = useId();

  const mutation = useMutation({ mutationFn: signInWithEmail });

  useEffect(() => {
    if (errorMessage !== null) {
      errorSummaryRef.current?.focus();
    }
  }, [errorMessage]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setErrorMessage(null);

    mutation.mutate(
      { email, password },
      {
        onSuccess: (result) => {
          if (!result.ok) {
            // §5: „formularz zachowuje dane po błędzie" — `email`/`password` NIE są czyszczone
            // tutaj ani w żadnej innej gałęzi błędu.
            setErrorMessage(GENERIC_ERROR_MESSAGE);
            isSubmittingRef.current = false;
            return;
          }

          const target =
            search.redirect !== undefined && isSafeInternalPath(search.redirect)
              ? search.redirect
              : '/tree';

          void queryClient.invalidateQueries({ queryKey: sessionQueryKey }).then(() => {
            // Ref celowo NIE jest resetowany na tej ścieżce sukcesu — komponent zaraz się
            // odmontuje wraz z nawigacją, więc kolejne kliknięcie nie może już wystąpić.
            void navigate({ to: target });
          });
        },
        onError: () => {
          setErrorMessage(GENERIC_ERROR_MESSAGE);
          isSubmittingRef.current = false;
        },
      },
    );
  }

  const isBusy = mutation.isPending;
  const describedBy = errorMessage !== null ? errorSummaryId : undefined;

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-8">
      <div className="w-full max-w-md rounded border border-border bg-surface p-6 sm:p-8">
        <h1 className="font-heading text-3xl text-ink">Rodzinne Archiwum</h1>
        <p className="mt-1 text-ink-muted">Historia zachowana dla rodziny</p>

        <form className="mt-6 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {errorMessage !== null ? (
            <div
              ref={errorSummaryRef}
              id={errorSummaryId}
              role="alert"
              tabIndex={-1}
              className="rounded border border-danger px-3 py-2 text-sm text-danger"
            >
              {errorMessage}
            </div>
          ) : null}

          <div className="flex flex-col gap-1">
            <label htmlFor="login-email" className="text-sm font-medium text-ink">
              E-mail
            </label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              aria-describedby={describedBy}
              className="h-11 rounded border border-border-strong bg-surface px-3 text-ink"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="login-password" className="text-sm font-medium text-ink">
              Hasło
            </label>
            <div className="flex gap-2">
              <input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                aria-describedby={describedBy}
                className="h-11 flex-1 rounded border border-border-strong bg-surface px-3 text-ink"
              />
              <button
                type="button"
                aria-pressed={showPassword}
                onClick={() => {
                  setShowPassword((value) => !value);
                }}
                className="h-11 min-w-11 rounded border border-border-strong px-3 text-sm text-ink"
              >
                {showPassword ? 'Ukryj' : 'Pokaż'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isBusy}
            aria-busy={isBusy}
            className="h-11 rounded bg-accent px-4 font-medium text-white disabled:opacity-70"
          >
            {isBusy ? 'Logowanie…' : 'Zaloguj się'}
          </button>
        </form>

        <div className="mt-4 text-sm">
          <button
            type="button"
            className="min-h-11 text-accent underline"
            aria-expanded={showForgotPasswordHelp}
            onClick={() => {
              setShowForgotPasswordHelp((value) => !value);
            }}
          >
            Nie pamiętasz hasła?
          </button>
          {showForgotPasswordHelp ? (
            <p className="mt-2 text-ink-muted">
              Reset hasła wykonuje administrator archiwum — skontaktuj się z nim. Ta strona nigdy
              nie ujawnia, czy podany adres e-mail odpowiada istniejącemu kontu.
            </p>
          ) : null}
        </div>

        <p className="mt-6 border-t border-border pt-4 text-xs text-ink-muted">
          Dostęp wyłącznie dla zaproszonych osób.
        </p>
      </div>
    </main>
  );
}
